import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { PricingPlan, PricingPlanExtension } from 'types/pricing_plan';

import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useAPI } from 'hooks/api/useAPI';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';

type UseCurrentPlanOptions = UseQueryOptions<PricingPlan | null, APIError>;

export function useCurrentPlan({ enabled }: UseCurrentPlanOptions = {}) {
  const { client } = useAPI();
  const { data: organization } = useCurrentOrganization();

  const organizationId = organization?.id;

  const queryResult = useAuthenticatedQuery(
    ['pricing-plan', organizationId],
    async () => {
      if (!organizationId) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.get<PricingPlan | null>(
        `/internal/orgs/${organizationId}/plan`
      );

      if (!data?.description) return data;

      // Attempt to parse the plan description as a JSON object
      // If it fails, return the plan as is
      try {
        const parsedExtension = JSON.parse(
          data.description
        ) as PricingPlanExtension;
        return {
          ...data,
          description: parsedExtension.tagline,
          extension: parsedExtension,
        };
      } catch {
        return data;
      }
    },
    {
      enabled:
        enabled !== undefined
          ? Boolean(organizationId) && enabled
          : Boolean(organizationId),
    }
  );

  const isStarterPlan =
    queryResult.data?.id === import.meta.env.VITE_STARTER_PLAN_ID;
  const isBusinessPlan =
    queryResult.data?.id === import.meta.env.VITE_BUSINESS_PLAN_ID;
  const isCustomPlan = queryResult.data === null;

  return {
    ...queryResult,
    isStarterPlan,
    isBusinessPlan,
    isCustomPlan,
  };
}
